import { Input, Table, Tooltip } from "antd";
import React, { FC, useCallback, useEffect, useState } from "react";
import "./index.scss";
import { SearchOutlined } from "@ant-design/icons";
import downloadIcon from "../../../../assets/svg/downloadIcon.svg";
import infoIcon from "../../../../assets/svg/infoIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import { RaiseAdRequestState } from "../../../../store/types/raiseAdRequest";
import { useHistory, useParams } from "react-router-dom";
import { formatDate } from "../../../AgencyFileDrop/utils";
import InfiniteScroll from "react-infinite-scroll-component";
import { fetchAllRaiseAdRequest } from "../../../../store/actions/raiseAdRequest";
import { AuthState } from "../../../../store/types";
import { openToast } from "../../../../Toasts";
import { handleRequestDownload } from "../../utils";

const permittedRoles = ["admin", "product-admin"];

export const RequestsDashboard: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProductId } = useParams<{ selectedProductId: string }>();
  const { allRequests, userRole } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );
  const { token } = useSelector(
    (state: { authentication: AuthState }) => state.authentication
  );

  const isUserPermitted = permittedRoles.includes(userRole);
  const { count, results } = allRequests;
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [page, setPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");

  const handleViewRequest = useCallback(
    (requestId) => {
      history.push(
        `/cocreate/workflow/raise-notice-ad/${selectedProductId}/request-creation/${requestId}`
      );
    },
    [history, selectedProductId]
  );

  // pagination fetch
  const fetchMoreData = useCallback(() => {
    dispatch(
      fetchAllRaiseAdRequest({
        productId: selectedProductId,
        page: page + 1,
        search: searchText, // pass search term
      })
    );
    setPage((p) => p + 1);
  }, [dispatch, page, selectedProductId, searchText]);

  // ✅ Debounce search
  useEffect(() => {
    if (!searchText.length) return; // ⛔ skip if empty

    const handler = setTimeout(() => {
      setPage(1);
      dispatch(
        fetchAllRaiseAdRequest({
          productId: selectedProductId,
          page: 1,
          search: searchText || undefined,
        })
      );
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, dispatch, selectedProductId]);

  const handleSearchText = useCallback(
    (e) => {
      if (e.target.value === "") {
        dispatch(
          fetchAllRaiseAdRequest({
            productId: selectedProductId,
            page: 1,
          })
        );
      }
      setSearchText(e.target.value);
    },
    [dispatch, selectedProductId]
  );

  const columns = [
    {
      title: "Request Name",
      dataIndex: "request_name",
      key: "request_name",
      ellipsis: true,
      render: (_, record) => (
        <div className="request-name">
          <Tooltip placement="bottom" title={record.request_name}>
            <div className="request-name__title">{record.request_name}</div>
          </Tooltip>
          <div className="request-name__type">{record.request_type}</div>
        </div>
      ),
    },
    {
      title: "City, State",
      dataIndex: "state",
      key: "state",
      ellipsis: true,
      render: (_, record) => (
        <div className="request-stage">
          <Tooltip placement="bottom" title={record.state}>
            <div className="request-stage__text">
              {record.city}, {record.state}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      render: (_, record) => {
        const displayStage = !!record.trashed_at ? "Canceled" : record.stage;

        return (
          <div className="request-stage">
            <div className="request-stage__text">{displayStage}</div>
            <Tooltip placement="bottom" title={displayStage}>
              <img src={infoIcon} alt="Info" className="request-stage__icon" />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Last Modified On",
      dataIndex: "modified_at",
      key: "modified_at",
      render: (_, record) => (
        <div className="request-stage">
          <div className="request-stage__text">
            {formatDate(record.modified_at)}
          </div>
        </div>
      ),
    },
    {
      title: "Request By",
      dataIndex: "requested_by_name",
      key: "requested_by_name",
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      render: (_, record) => (
        <div className="request-stage">
          <div className="request-stage__text">
            {formatDate(record.created_at)}
          </div>
        </div>
      ),
    },
    {
      title: "Publish Date",
      dataIndex: "final_release_date",
      key: "final_release_date",
      render: (_, record) => (
        <div className="request-stage">
          <div className="request-stage__text">
            {record.final_release_date
              ? formatDate(record.final_release_date)
              : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <div className="request-action">
          <button
            className="btn btn--white"
            onClick={() => handleViewRequest(record.req_id)}
          >
            View Request
          </button>
          {!!isUserPermitted ? (
            <Tooltip placement="bottom" title="Download">
              <img
                src={downloadIcon}
                alt="Download"
                className="request-action__icon"
                onClick={() => handleRequestDownload(record.req_id, token)}
              />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      if (keys.length <= 5) {
        setSelectedRowKeys(keys);
      } else {
        openToast("info", "You can select only 5 requests at a time.");
      }
    },
  };

  return (
    <>
      <div className="requests-dashboard__header">
        <div className="requests-dashboard__title">All Requests</div>
        <div className="requests-dashboard__actions">
          <Input
            placeholder="Search ..."
            value={searchText}
            onChange={(e) => handleSearchText(e)}
            suffix={<SearchOutlined style={{ color: "rgba(0,0,0,0.45)" }} />}
            className="requests-dashboard__search"
          />
          {!!isUserPermitted ? (
            <button
              disabled={!selectedRowKeys.length}
              className="btn btn--purple"
              onClick={() => handleRequestDownload(selectedRowKeys, token)}
            >
              Download
            </button>
          ) : null}
        </div>
      </div>

      <div id="scrollableDiv" style={{ height: "68rem", overflow: "auto" }}>
        <InfiniteScroll
          dataLength={results.length}
          next={fetchMoreData}
          hasMore={results.length < count}
          loader={<></>}
          scrollableTarget="scrollableDiv"
        >
          <Table
            className="table-custom request-table"
            rowSelection={isUserPermitted ? rowSelection : undefined}
            dataSource={results}
            columns={columns}
            pagination={false}
            rowKey="req_id"
            rowClassName={(record) =>
              record.stage === "Review and Close"
                ? "row-final-stage"
                : ""
            }
          />
        </InfiniteScroll>
      </div>
    </>
  );
};
